import { useCallback } from "react";
import { useRouter } from "@tanstack/react-router";

import { useAppNavigate, type AppNavigateOptions } from "@/lib/use-app-navigate";

export type ViewerExitTarget = Pick<AppNavigateOptions, "to" | "params" | "search" | "title">;

export interface UseViewerExitOptions {
  /** Where to go when there is no origin to return to (usually the pilot's job list). */
  fallback: ViewerExitTarget;
  /** Explicit origin (e.g. the RevPilot comparison that opened the viewer). Wins over history. */
  returnTo?: ViewerExitTarget | null;
}

/**
 * Exit handler shared by the full-screen viewers (`ViewerBackButton`). Returns to an explicit
 * origin when one is given, otherwise steps back through history when the previous entry is
 * in-app, otherwise navigates to the fallback list.
 */
export function useViewerExit({ fallback, returnTo }: UseViewerExitOptions) {
  const navigate = useAppNavigate();
  const router = useRouter();
  const { to: fallbackTo, title: fallbackTitle } = fallback;
  const returnToTo = returnTo?.to;
  const returnToParams = returnTo?.params;
  const returnToSearch = returnTo?.search;
  const returnToTitle = returnTo?.title;

  return useCallback(() => {
    if (returnToTo) {
      navigate({
        to: returnToTo,
        params: returnToParams,
        search: returnToSearch,
        title: returnToTitle,
      });
      return;
    }
    if (router.history.canGoBack()) {
      router.history.back();
      return;
    }
    navigate({ to: fallbackTo, title: fallbackTitle });
  }, [fallbackTitle, fallbackTo, navigate, returnToParams, returnToSearch, returnToTitle, returnToTo, router]);
}
